import { useMemo } from 'react';

import {
  CANDIDATES_LIST_GROUP_BY_LOCALSTORAGE_KEY,
  OrderDirection,
} from '@constants';
import { canonicalSeniority, candidateFacetKey, CandidateRegion } from '@utils';
import { CandidateColumnId, isCandidateColumnId } from '@components';
import {
  IListViewBase,
  IListViewConfig,
  splitParam,
  useListViewState,
} from './useListViewState';

export type CandidateGroupingOptionType =
  | 'workstream-position'
  | 'position'
  | 'workstream'
  | 'seniority'
  | 'status'
  | 'none';

interface ICandidateFilters {
  positionIds: string[];
  regions: CandidateRegion[];
  seniorities: string[];
  statuses: string[];
  showRejected: boolean;
}

export interface ICandidatesViewState
  extends IListViewBase<CandidateGroupingOptionType, CandidateColumnId>,
    ICandidateFilters {}

const DefaultCandidateSort = {
  key: 'status',
  direction: OrderDirection.ASC,
};

export const EmptyCandidateFilters: Pick<
  ICandidatesViewState,
  'positionIds' | 'regions' | 'seniorities' | 'statuses'
> = {
  positionIds: [],
  regions: [],
  seniorities: [],
  statuses: [],
};

export const isDefaultSort = (sort: ICandidatesViewState['sort']): boolean =>
  sort.key === DefaultCandidateSort.key &&
  sort.direction === DefaultCandidateSort.direction;

const GroupingOptions: CandidateGroupingOptionType[] = [
  'workstream-position',
  'position',
  'workstream',
  'seniority',
  'status',
  'none',
];

export const useCandidatesViewState = (
  defaultColumns: CandidateColumnId[],
  knownPositionIds: string[],
) => {
  const config = useMemo(
    (): IListViewConfig<
      ICandidateFilters,
      CandidateGroupingOptionType,
      CandidateColumnId
    > => ({
      emptyFilters: { ...EmptyCandidateFilters, showRejected: false },
      readFilters: (params) => ({
        positionIds: splitParam(params.get('positions')).filter((id) =>
          knownPositionIds.includes(id),
        ),
        regions: splitParam(params.get('region')).filter(
          (value): value is CandidateRegion =>
            Object.values(CandidateRegion).includes(value as CandidateRegion),
        ),
        seniorities: splitParam(params.get('seniority')).map(
          canonicalSeniority,
        ),
        statuses: splitParam(params.get('status')).map(candidateFacetKey),
        showRejected: params.get('rejected') === 'true',
      }),
      writeFilters: (filters, set) => {
        set('positions', filters.positionIds.join(','));
        set('region', filters.regions.join(','));
        set('seniority', filters.seniorities.join(','));
        set('status', filters.statuses.join(','));
        set('rejected', filters.showRejected ? 'true' : null);
      },
      countFilters: (filters) =>
        filters.positionIds.length +
        filters.regions.length +
        filters.seniorities.length +
        filters.statuses.length,
      groupOptions: GroupingOptions,
      defaultGroup: 'position',
      groupStorageKey: CANDIDATES_LIST_GROUP_BY_LOCALSTORAGE_KEY,
      defaultColumns,
      isColumn: isCandidateColumnId,
      defaultSort: DefaultCandidateSort,
    }),
    [defaultColumns, knownPositionIds],
  );

  return useListViewState(config);
};
